import { all, call, put, retry, takeEvery, takeLatest } from 'redux-saga/effects';
import ACTIONS from '../app.constants';
import { API_CONFIGURATIONS, Api } from '../services/services';
import { getAccessToken } from './authentication.sagas';
import {
    ApplicationActionType,
    Form,
    ViolationUpdateActionType,
    Violation,
    AddViolationCommentActionType,
    FormTypeConfiguration,
    CommentList,
    Comment,
    ViolationList,
} from '../modules/application/types';
import { licenseActions, siteActions, violationActions } from '../actions';
import violationDefaultEditForm from '../../views/ViolationDetailTabs/Tabs/ViolationData/violationDefaultEditForm.json';

function* getViolationDetailById(action: ApplicationActionType) {
    const id: string = action.payload.id;
    let violation: Violation;
    let editForm: Form = violationDefaultEditForm;
    let violationType: FormTypeConfiguration;
    try {
        yield put(violationActions.getViolationDetail.pending);
        const violationToken: string = yield call(getAccessToken, 'violations.read');
        violation = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getViolationDetail,
            violationToken,
            id,
        );

        const violationCategoriesToken: string = yield call(getAccessToken, 'violationCategories.read');
        violationType = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getViolationCategory,
            violationCategoriesToken,
            violation.category.id,
        );

        if (violationType.views?.edit?.formId) {
            try {
                const formsToken: string = yield call(getAccessToken, 'forms.read');
                editForm = yield retry(
                    API_CONFIGURATIONS.MAX_RETRY_COUNT,
                    API_CONFIGURATIONS.RETRY_INTERVAL,
                    Api.getForm,
                    formsToken,
                    violationType.views?.edit?.formId,
                );
            } catch (error) {
                yield put(
                    siteActions.setError({
                        isError: false,
                        errorCode: 0,
                        message: 'Something went wrong. Please contact IT support.',
                    }),
                );
            }
        }
        yield put(violationActions.getViolationDetail.success(violation, editForm));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(violationActions.getViolationDetail.error(error));
    }
}

function* updateViolation(action: ViolationUpdateActionType) {
    const data: Violation = action.payload.data;
    const violationId: string = action.payload.violationId;
    try {
        yield put(violationActions.updateViolation.pending);
        const token: string = yield call(getAccessToken, 'violations.write');
        yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.updateViolation,
            token,
            data,
            violationId,
        );
        yield put(violationActions.updateViolation.success(action.payload.data));
        yield put(violationActions.getViolationDetailsBegin(violationId));
        yield put(siteActions.setSuccess({ isError: false, errorCode: 0, message: 'Information has been saved.' }));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(violationActions.updateViolation.error(error));
    }
}

function* addViolationComment(action: AddViolationCommentActionType) {
    const data: string = action.payload.data;
    const violationId: string = action.payload.violationId;
    try {
        yield put(violationActions.addViolationComment.pending);
        const token: string = yield call(getAccessToken, 'violations.write');
        const result: Comment = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.addViolationComment,
            token,
            data,
            violationId,
        );
        yield put(violationActions.addViolationComment.success(result));
        yield put(
            siteActions.setSuccess({ isError: false, errorCode: 0, message: 'Violation comment has been added.' }),
        );
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(violationActions.addViolationComment.error(error));
    }
}

function* getViolationCommentList(action: ApplicationActionType) {
    const violationId: string = action.payload.id;
    try {
        yield put(violationActions.getViolationCommentList.pending);
        const token: string = yield call(getAccessToken, 'violations.read');
        const response: CommentList = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getViolationCommentList,
            token,
            violationId,
            action.paginationData,
        );
        yield put(
            violationActions.getViolationCommentList.success(
                response.length > 0 ? response : [],
                action.paginationData,
            ),
        );
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(violationActions.getViolationCommentList.error(error));
    }
}

function* getOpenedViolation(action: ApplicationActionType) {
    const licenseId: string = action.payload.licenseId;
    try {
        yield put(violationActions.getOpenedViolations.pending);
        const token: string = yield call(getAccessToken, 'violations.read');
        const response: ViolationList = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getOpenedViolations,
            token,
            licenseId,
            action.paginationData,
        );
        yield put(violationActions.getOpenedViolations.success(response.length > 0 ? response : []));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(violationActions.getOpenedViolations.error(error));
    }
}

function* closeBulkViolations(action: ApplicationActionType) {
    try {
        yield put(violationActions.closeBulkViolations.pending);
        const token: string = yield call(getAccessToken, 'violations.write');
        const response: ViolationList = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.closeBulkViolations,
            token,
            action.payload.data,
        );
        yield put(licenseActions.getViolationListBegin(action.payload.licenseId, { limit: 20, skip: 0 }));
        yield put(violationActions.closeBulkViolations.success(response.length > 0 ? response : []));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(violationActions.closeBulkViolations.error(error));
    }
}

export default function* rootSaga() {
    yield all([
        takeEvery(ACTIONS.GET_VIOLATION_DETAIL_BY_ID_BEGIN, getViolationDetailById),
        takeLatest(ACTIONS.UPDATE_VIOLATION_BEGIN, updateViolation),
        takeLatest(ACTIONS.ADD_VIOLATION_COMMENT_BEGIN, addViolationComment),
        takeLatest(ACTIONS.VIOLATION_COMMENT_LIST_BEGIN, getViolationCommentList),
        takeLatest(ACTIONS.GET_OPENED_VIOLATIONS_BEGIN, getOpenedViolation),
        takeLatest(ACTIONS.CLOSE_BULK_VIOLATIONS_BEGIN, closeBulkViolations),
    ]);
}
